// src/components/ResourceManager/IpBlock/IpInventoryPage.tsx

import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericAlert from "@ui-components/Alert/GenericAlert";

// Icons
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";

// The interface & columns from IpData
import { IpBlock, columns as baseColumns } from "./IpData";

// Your request functions (you'd replace with real ones)
import {
	getIpBlocks,
	editIp, // rename to updateIp if needed
	deleteIp,
	addIp, // if you also have a create IP endpoint
} from "./request/request";
import { getProviderIds } from "../DataCenter/request/request";

interface ProviderOption {
	id: number;
	name: string;
}
export default function IpInventoryPage() {
	// ========== 1) Table Data ==========
	const [ipBlocks, setIpBlocks] = useState<IpBlock[]>([]);
	const [isFetching, setIsFetching] = useState(false);

	// ========== 2) Add/Edit modal state & fields ==========
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);

	// IP block fields (for Add/Edit)
	const [ipId, setIpId] = useState<number>(0);
	const [ipName, setIpName] = useState("");
	const [ipAddress, setIpAddress] = useState("");
	const [ipMask, setIpMask] = useState<number>(0);
	const [operationType, setOperationType] = useState("internal"); // "internal" or "reselling"
	const [dcProvider, setDcProvider] = useState("");
	const [providerId, setProviderId] = useState<number>(0);
	const [countryId, setCountryId] = useState<string>("");
	const [datacenterId, setDatacenterId] = useState<string>(""); // For "reselling" operation type
	const [ipTypeId, setIpTypeId] = useState<number>(1); // default to IPv4 (1)
	// ========== 3) View-only modal ==========
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedIpBlock, setSelectedIpBlock] = useState<IpBlock | null>(null);

	// ========== 4) Delete confirmation ==========
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [ipToDelete, setIpToDelete] = useState<IpBlock | null>(null);

	// ========== Auth0 ==========
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

	const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

	//Provider Dropdown Handling logic
	// 1) Convert provider objects to an array of strings for the dropdown
	const providerNames = providerOptions.map((opt) => opt.name);

	// 2) Find the name associated with the currently selected provider ID
	function getProviderNameById(providerId: string): string {
		const match = providerOptions.find((opt) => String(opt.id) === providerId);
		return match ? match.name : "";
	}

	// 3) When the user picks a name, find its ID and store it
	function handleSelectProviderName(selectedName: string) {
		const match = providerOptions.find((opt) => opt.name === selectedName);
		setProviderId(match ? match.id : 0)
		setDcProvider(match ? String(match.id) : "")
	}

	// ------------------------------------------------------------------------------------
	// useEffect: call GET /ip-blocks
	// ------------------------------------------------------------------------------------
	useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) return;

		(async () => {
			try {
				setIsFetching(true);
				const token = await getAccessTokenSilently();
				const resp = await getIpBlocks(token);
				// Suppose resp.data is an array of IpBlock
				setIpBlocks(resp.data);
			} catch (err) {
				console.error("Error fetching IP blocks:", err);
			} finally {
				setIsFetching(false);
			}
		})();

		const fetchProviderOptions = async () => {
			try {
				const token = await getAccessTokenSilently();
				// getProviderIds should return an array
				const providerResp = await getProviderIds(token);
				setProviderOptions(providerResp.data); // store them in state
			} catch (error) {
				console.error("Error fetching provider IDs:", error);
			}
		};

		fetchProviderOptions();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	// ------------------------------------------------------------------------------------
	// Handle ADD
	// ------------------------------------------------------------------------------------
	function handleAddIpBlock() {
		setEditMode(false);
		// Clear fields
		setIpName("");
		setIpAddress("");
		setIpMask(0);
		setOperationType("internal");
		setProviderId(0);
		setCountryId("");
		setIsModalOpen(true);
	}

	// ------------------------------------------------------------------------------------
	// Handle EDIT
	// ------------------------------------------------------------------------------------
	function handleEditIpBlock(row: IpBlock) {
		setEditMode(true);
		setIpId(row.id);
		setIpName(row.name);
		setIpAddress(row.ip);
		setIpMask(row.mask);
		setOperationType(row.operationType);
		setProviderId(row.providerId);
		setCountryId(row.countryId);
		setIsModalOpen(true);
	}

	// ------------------------------------------------------------------------------------
	// Handle SAVE (ADD / UPDATE)
	// ------------------------------------------------------------------------------------
	async function handleSaveIpBlock() {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			// Build the payload
			const payload = {
				id: ipId,
				name: ipName,
				ip: ipAddress,
				mask: ipMask,
				operationType,
				providerId: providerId,
				countryId: countryId,
				ipTypeId, // send 1 for IPv4, 2 for IPv6
				...(operationType === "reselling" && { datacenterId }), // conditionally add datacenterId
			};
			if (editMode) {
				// Update
				await editIp(token, ipId, payload);
				// Update local state
				setIpBlocks((prev) =>
					prev.map((b) => (b.id === ipId ? { ...b, ...payload } : b))
				);
			} else {
				await addIp(token, payload);
        const resp = await getIpBlocks(token)
				setIpBlocks((prev) => [...prev, resp.data]);
			}
		} catch (error) {
			console.error("Error saving IP block:", error);
		} finally {
			setIsFetching(false);
			setIsModalOpen(false);
		}
	}

	// ------------------------------------------------------------------------------------
	// Handle VIEW
	// ------------------------------------------------------------------------------------
	function handleViewIpBlock(row: IpBlock) {
		setSelectedIpBlock(row);
		setIsViewModalOpen(true);
	}

	// ------------------------------------------------------------------------------------
	// Handle DELETE
	// ------------------------------------------------------------------------------------
	function handleDeleteClick(row: IpBlock) {
		setIpToDelete(row);
		setIsAlertOpen(true);
	}

	async function handleConfirmDelete() {
		if (!ipToDelete) return;
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			await deleteIp(token, ipToDelete.id);

			// Remove from local table
			setIpBlocks((prev) => prev.filter((b) => b.id !== ipToDelete.id));
		} catch (error) {
			console.error("Error deleting IP block:", error);
		} finally {
			setIsFetching(false);
			setIpToDelete(null);
			setIsAlertOpen(false);
		}
	}

	function handleCancelDelete() {
		setIpToDelete(null);
		setIsAlertOpen(false);
	}

	// ------------------------------------------------------------------------------------
	// Override "Actions" column
	// ------------------------------------------------------------------------------------
	const columns = baseColumns.map((col) => {
		if (col.header === "Actions" && col.renderCell) {
			return {
				...col,
				renderCell: (row: IpBlock) => (
					<div className="flex items-center gap-2">
						{/* View */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleViewIpBlock(row)}
						>
							<IconEye className="w-6 h-6" />
						</button>

						{/* Edit */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleEditIpBlock(row)}
						>
							<IconEdit className="w-10 h-10" />
						</button>

						{/* Delete */}
						<button
							className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
							onClick={() => handleDeleteClick(row)}
						>
							<IconDelete className="w-6 h-6" />
						</button>
					</div>
				),
			};
		}
		return col;
	});

	// ------------------------------------------------------------------------------------
	// RENDER
	// ------------------------------------------------------------------------------------
	return (
		<div className="relative p-4">
			{/* Header */}
			<div className="mb-4 p-4 bg-[var(--semantic-bg-primary)]">
				<div className="flex items-end justify-between">
					<div className="flex items-end space-x-4">
						{/* Optionally, a dropdown filter for "All", "internal", "reselling"? */}
					</div>
					<GenericButton
						label={
							<div className="flex items-center gap-2">
								<IconPlus className="w-4 h-4" />
								<span>Add new IP block</span>
							</div>
						}
						variant="brand"
						size="md"
						onClick={handleAddIpBlock}
					/>
				</div>
			</div>

			{/* Table */}
			<GenericTable
				columns={columns}
				data={ipBlocks}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				tableClassName="bg-[var(--semantic-bg-primary)]"
				width="100%"
			/>

			{/* ====================== Modal Add / Edit ====================== */}
			<GenericModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={editMode ? "Edit IP block" : "Add IP block"}
				size="md"
			>
				<div className="grid grid-cols-2 gap-4 mb-4">
					<GenericInput
						label="Name/Description"
						value={ipName}
						onChange={setIpName}
					/>
					<GenericInput label="IP" value={ipAddress} onChange={setIpAddress} />
					<GenericInput
						label="Mask"
						value={String(ipMask)}
						onChange={ (val) => setIpMask(Number(val))}
					/>
					{/* Conditionally render the Datacenter ID input if operationType is "reselling" */}
					{operationType === "reselling" && (
						<GenericInput
							label="Datacenter ID"
							value={datacenterId}
							onChange={setDatacenterId}
						/>
					)}
					{/* Example: operationType dropdown */}
					<div className="flex flex-col space-y-2">
						<label className="text-sm font-medium">Type</label>
						<GenericDropdown
							items={["internal", "reselling"]}
							value={operationType}
							onSelect={(val) => setOperationType(val)}
							defaultLabel="Pick operation type..."
              widthClass="w-full"
						/>
					</div>

					<div className="flex flex-col space-y-2">
						<label className="text-sm font-medium">Provider</label>
						<GenericDropdown
							items={providerNames} 
							value={getProviderNameById(dcProvider)} // convert ID -> name
							onSelect={handleSelectProviderName} // convert name -> ID
							defaultLabel="Select a Provider"
							widthClass="w-full"
						/>
					</div>

					{/* IP Type Dropdown */}
					<div className="flex flex-col space-y-2">
						<label className="text-sm font-medium">IP Type</label>
						<GenericDropdown
							items={["IPv4", "IPv6"]}
							value={ipTypeId === 1 ? "IPv4" : "IPv6"}
							onSelect={(val) => {
								if (val === "IPv4") setIpTypeId(1);
								else if (val === "IPv6") setIpTypeId(2);
							}}
							defaultLabel="Select IP Type"
						/>
					</div>
					<div className="flex flex-col space-y-2">
						<label className="text-sm font-medium">Country ID</label>
						<GenericDropdown
							items={["MX"]}
							value={countryId}
							onSelect={setCountryId}
							defaultLabel="Select Country"
						/>
					</div>
				</div>

				<div className="flex justify-end gap-2">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsModalOpen(false)}
					>
						Cancel
					</button>
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              hover:bg-opacity-90
              transition-colors
            "
						onClick={handleSaveIpBlock}
					>
						Save
					</button>
				</div>
			</GenericModal>

			{/* ====================== Modal View (read-only) ====================== */}
			<GenericModal
				isOpen={isViewModalOpen}
				onClose={() => setIsViewModalOpen(false)}
				title="IP Block Details"
				size="md"
			>
				{selectedIpBlock && (
					<div className="grid grid-cols-2 gap-4 mb-4">
						<GenericInput
							label="ID"
							value={String(selectedIpBlock.id)}
							readOnly
						/>
						<GenericInput
							label="Name/Description"
							value={selectedIpBlock.name}
							readOnly
						/>
						<GenericInput label="IP" value={selectedIpBlock.ip} readOnly />
						<GenericInput
							label="Mask"
							value={String(selectedIpBlock.mask)}
							readOnly
						/>
						<GenericInput
							label="Operation Type"
							value={selectedIpBlock.operationType}
							readOnly
						/>
						<GenericInput
							label="Provider"
							value={
								selectedIpBlock.provider ? selectedIpBlock.provider.name : ""
							}
							readOnly
						/>
						<GenericInput
							label="Country"
							value={
								selectedIpBlock.country ? selectedIpBlock.country.name : ""
							}
							readOnly
						/>
						<GenericInput
							label="IP Version"
							value={
								selectedIpBlock.ipVersion ? selectedIpBlock.ipVersion.name : ""
							}
							readOnly
						/>
					</div>
				)}
				<div className="flex justify-end">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsViewModalOpen(false)}
					>
						Close
					</button>
				</div>
			</GenericModal>

			{/* ====================== ALERT: confirm Delete ====================== */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the IP block from the inventory."
				cancelLabel="Cancel"
				confirmLabel="Delete"
				iconWidthClass="w-20 h-12"
			/>

			{/* Overlay semi-transparente sólo si isFetching */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}
